 
/**
 * Datadog Browser RUM + Logs initialization.
 *
 * For a SPA, Datadog "APM" coverage is provided by Browser RUM, which:
 *  - captures page views, errors, long tasks, resources, web vitals
 *  - auto-instruments fetch / XHR
 *  - injects distributed tracing headers (x-datadog-*, traceparent) so
 *    requests are stitched to backend APM traces (when the backend
 *    services are also instrumented with Datadog tracers)
 *
 * All configuration is driven by VITE_DD_* env vars. If
 * VITE_DD_RUM_APPLICATION_ID or VITE_DD_CLIENT_TOKEN are missing,
 * initialization is skipped (safe no-op) so the app still boots in
 * environments without Datadog credentials.
 *
 * Sodexo runs on the EU Datadog org (datadoghq.eu) so that's the default.
 */
import { datadogRum, type RumInitConfiguration } from '@datadog/browser-rum';
import { datadogLogs, type LogsInitConfiguration } from '@datadog/browser-logs';
import { redactDynamicUrl } from './resolveDatadogViewName';
import { redactSensitiveText } from './telemetryRedaction';

let initialized = false;

const DEFAULT_DD_SITE = 'datadoghq.eu';

/**
 * Intake endpoints of the OTHER telemetry SDKs that run in this page.
 *
 * The B2O container is a micro-frontend host and each sibling module boots its
 * own Application Insights instance, so the page continuously POSTs to
 * `dc.services.visualstudio.com/v2/track` (measured in dev: ~44 requests/minute
 * at rest, bursting past 270/minute), re-fetches the AI remote config from
 * `js.monitor.azure.com`, and beacons Piano events to `*.pa-cd.com`. With
 * trackResources:true the RUM SDK faithfully records every one of those as a
 * `resource` event — over a 7h dev window that was 1067 of 1507 resource events
 * (71%), i.e. two thirds of all RUM ingestion was telemetry ABOUT telemetry,
 * carrying no information about our own app's performance.
 *
 * The SDK already excludes its own intake from resource tracking; this extends
 * the same rule to the co-resident SDKs. Dropping the events in `beforeSend`
 * also shrinks intake traffic proportionally, because RUM batches are flushed
 * by accumulated size/count.
 *
 * Matches an optional subdomain then one of the hosts, and requires a host
 * terminator (`/`, `?`, `#`, `:port`, end) so a lookalike domain such as
 * `pa-cd.com.example.org` is NOT excluded. No `g` flag: the SDK reuses this
 * matcher for excludedActivityUrls, and a stateful regex would alternate
 * match/no-match across calls.
 */
const THIRD_PARTY_TELEMETRY_URL_RE =
  /^https?:\/\/(?:[^/?#]+\.)?(?:dc\.services\.visualstudio\.com|js\.monitor\.azure\.com|pa-cd\.com)(?::\d+)?(?:[/?#]|$)/i;

/**
 * RUM `beforeSend`: scrubs everything the SDK captures natively (i.e. NOT via
 * our helpers). (1) view / resource URLs still carry raw contract/site ids +
 * query params even though `view.name` is bucketed by resolveDatadogViewName;
 * (2) uncaught errors reach RUM without passing through normalizeErrorForTelemetry.
 * Collapses dynamic URL segments + strips query/hash, and redacts PII/secret
 * patterns from error message/stack. Returns false to DISCARD an event (used for
 * the other SDKs' intake traffic). Typed from the SDK config so the event shape
 * stays correct; exported for unit testing.
 */
export const redactRumEvent: NonNullable<RumInitConfiguration['beforeSend']> = (event) => {
  if (
    event.type === 'resource' &&
    event.resource?.url &&
    THIRD_PARTY_TELEMETRY_URL_RE.test(event.resource.url)
  ) {
    return false;
  }
  if (event.view) {
    if (event.view.url) event.view.url = redactDynamicUrl(event.view.url);
    if (event.view.referrer) event.view.referrer = redactDynamicUrl(event.view.referrer);
  }
  if (event.type === 'resource' && event.resource?.url) {
    event.resource.url = redactDynamicUrl(event.resource.url);
  }
  if (event.type === 'error' && event.error) {
    if (event.error.message) event.error.message = redactSensitiveText(event.error.message);
    if (event.error.stack) event.error.stack = redactSensitiveText(event.error.stack);
  }
  // With trackUserInteractions:true the SDK derives an action's name from the
  // clicked element's text/aria-label, which on this B2B surface can include
  // employee/customer/site labels (table cells, buttons). Scrub the same
  // PII/secret patterns we strip elsewhere. For genuinely free-text controls,
  // set an explicit `data-dd-action-name` in the DOM to override the computed
  // name at the source (pattern redaction can't catch arbitrary human names).
  if (event.type === 'action' && event.action?.target?.name) {
    event.action.target.name = redactSensitiveText(event.action.target.name);
  }
  return true;
};

/**
 * Logs `beforeSend`: forwardErrorsToLogs:true means EVERY console.error +
 * uncaught error/rejection in the deployed SPA is ingested here — including
 * call sites we don't own (@sdx/* modules, third-party libs). Redacts PII/secret
 * patterns from the log message + error message/stack and collapses dynamic URL
 * segments before anything is sent. Complements the per-call-site scrubbing.
 */
export const redactLogsEvent: NonNullable<LogsInitConfiguration['beforeSend']> = (event) => {
  if (event.message) event.message = redactSensitiveText(event.message);
  if (event.error) {
    if (event.error.message) event.error.message = redactSensitiveText(event.error.message);
    if (event.error.stack) event.error.stack = redactSensitiveText(event.error.stack);
  }
  if (event.view?.url) event.view.url = redactDynamicUrl(event.view.url);
  return true;
};

const parseNumber = (
  value: string | undefined,
  fallback: number,
  bounds?: { min: number; max: number },
): number => {
  if (!value) return fallback;
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  if (bounds) return Math.max(bounds.min, Math.min(bounds.max, n));
  return n;
};

const parseList = (value: string | undefined): string[] =>
  (value ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

// Tracing URLs accept full http(s) prefixes only (the SDK uses them as
// startsWith() matchers). Drop and warn on entries that don't look like
// URLs so a typo'd `htps://...` doesn't silently disable header injection
// with no diagnostic.
const filterTracingUrls = (urls: string[]): string[] =>
  urls.filter((url) => {
    if (/^https?:\/\//i.test(url)) return true;
    console.warn(
      `[datadog] Ignoring non-HTTP(S) entry in VITE_DD_ALLOWED_TRACING_URLS: '${url}'`,
    );
    return false;
  });

export interface DatadogUser {
  id?: string;
  email?: string;
  name?: string;
  [key: string]: unknown;
}

export type DatadogLogLevel = 'debug' | 'info' | 'warn' | 'error';

// Minimal shape of the Datadog SDK's ProxyFn. A function taking only
// { path, parameters } is assignable to the SDK's wider ProxyFn (which also
// carries optional protocol/subdomain), so we deliberately keep it narrow.
type DatadogProxyFn = (options: { path: string; parameters: string }) => string;

// Builds a `proxy` function from VITE_DD_PROXY, or returns undefined when the
// var is empty/invalid (default: send intake straight to Datadog).
//
// WHY THIS EXISTS: the B2O app is a micro-frontend host. Sibling modules each
// initialize their OWN Application Insights instance with
// enableCorsCorrelation:true, and every one re-patches window.fetch/XHR to
// inject Request-Id / Request-Context / traceparent headers onto EVERY
// cross-origin request — including the Datadog RUM/Logs intake POSTs. Those
// extra headers turn the SDK's CORS-"simple" intake POST into a preflighted
// request that browser-intake-datadoghq.* rejects ("request header field
// request-id is not allowed") — a flood of "CORS error" rows on every
// fetch-transport batch. A per-instance correlationHeaderExcludedDomains fix in
// the container cannot cover the module instances. Routing intake through a
// SAME-ORIGIN path makes those POSTs same-origin, so no CORS preflight fires at
// all, regardless of how many App Insights instances patch fetch.
//
// Requires an edge passthrough (nginx / Azure Front Door) that forwards
// `<proxy>/api/v2/*` to `https://browser-intake-<site>`. Until that route
// exists, leave VITE_DD_PROXY empty so intake keeps going directly to Datadog.
const buildDatadogProxy = (
  rawValue: string | undefined,
): DatadogProxyFn | undefined => {
  const base = (rawValue ?? '').trim();
  if (!base) return undefined;

  // Accept an absolute https URL (https://host/dd) or an origin-relative path
  // (/dd). Origin-relative is preferred so a single build stays same-origin on
  // every domain (dev/uat/prd) without per-env rewriting.
  const isAbsolute = /^https?:\/\//i.test(base);
  if (!isAbsolute && !base.startsWith('/')) {
    console.warn(
      `[datadog] Ignoring VITE_DD_PROXY='${base}': must be an absolute https URL ` +
        "or an origin-relative path starting with '/'. Intake will go directly to Datadog.",
    );
    return undefined;
  }

  const origin = isAbsolute ? base : `${window.location.origin}${base}`;
  const proxyPrefix = origin.replace(/\/+$/, '');
  return (options) => `${proxyPrefix}${options.path}?${options.parameters}`;
};

// Correlation headers that App Insights (enableCorsCorrelation:true) injects on
// cross-origin fetch/XHR. These are what force the Datadog intake preflight.
const AI_CORRELATION_HEADERS = ['request-id', 'request-context', 'traceparent', 'tracestate'];

// Any Datadog browser intake host (rum/logs/replay, .eu or .com, incl. the
// session-replay.* subdomain). Datadog's own SDK never sets the correlation
// headers on these, so anything present was added by a co-resident App Insights.
const DD_INTAKE_HOST_RE = /browser-intake-datadoghq\.(eu|com)/i;

const stripCorrelationHeadersFromInit = (
  headers: HeadersInit | undefined,
): HeadersInit | undefined => {
  if (!headers) return headers;
  if (typeof Headers !== 'undefined' && headers instanceof Headers) {
    AI_CORRELATION_HEADERS.forEach((h) => headers.delete(h));
    return headers;
  }
  if (Array.isArray(headers)) {
    return headers.filter(
      ([key]) => !AI_CORRELATION_HEADERS.includes(String(key).toLowerCase()),
    );
  }
  const out: Record<string, string> = {};
  Object.keys(headers as Record<string, string>).forEach((key) => {
    if (!AI_CORRELATION_HEADERS.includes(key.toLowerCase())) {
      out[key] = (headers as Record<string, string>)[key];
    }
  });
  return out;
};

// Patch `window.fetch` so Datadog intake POSTs shed the correlation headers.
const installFetchIntakeGuard = (): void => {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  const currentFetch = window.fetch;
  if ((currentFetch as { __ddIntakeGuard?: boolean }).__ddIntakeGuard) return;

  const guardedFetch = function (
    this: unknown,
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> {
    let nextInit = init;
    try {
      const url =
        typeof input === 'string'
          ? input
          : input instanceof URL
            ? input.href
            : input && typeof (input as Request).url === 'string'
              ? (input as Request).url
              : '';

      if (DD_INTAKE_HOST_RE.test(url)) {
        // Headers can live on a Request input and/or on init — clean both.
        if (typeof Request !== 'undefined' && input instanceof Request) {
          AI_CORRELATION_HEADERS.forEach((h) => {
            try {
              input.headers.delete(h);
            } catch {
              /* immutable header guard — ignore */
            }
          });
        }
        if (init?.headers) {
          nextInit = { ...init, headers: stripCorrelationHeadersFromInit(init.headers) };
        }
      }
    } catch {
      /* never let the guard break telemetry */
    }
    return currentFetch.call(this, input as RequestInfo, nextInit);
  } as typeof window.fetch;

  (guardedFetch as { __ddIntakeGuard?: boolean }).__ddIntakeGuard = true;
  window.fetch = guardedFetch;
};

// Mirror the guard on XMLHttpRequest. Datadog's SDK sends intake via
// sendBeacon/fetch (not XHR) today, so this is belt-and-suspenders — it covers a
// co-resident App Insights instance (which patches XHR too) or any future SDK
// transport change. We record the request URL on `open()` and, for Datadog
// intake hosts, drop the App-Insights correlation headers in
// `setRequestHeader()`. Patching the prototype before AI loads means AI's XHR
// wrapper calls our patched methods, so the strip runs even under AI.
const installXhrIntakeGuard = (): void => {
  if (typeof XMLHttpRequest === 'undefined') return;
  const proto = XMLHttpRequest.prototype;
  const originalOpen = proto.open;
  if ((originalOpen as { __ddIntakeGuard?: boolean }).__ddIntakeGuard) return;
  const originalSetRequestHeader = proto.setRequestHeader;

  const patchedOpen = function (
    this: XMLHttpRequest & { __ddIntakeUrl?: string },
    method: string,
    url: string | URL,
    ...rest: unknown[]
  ): void {
    try {
      this.__ddIntakeUrl = typeof url === 'string' ? url : (url as URL)?.href ?? '';
    } catch {
      /* ignore */
    }
     
    return originalOpen.apply(this, [method, url, ...rest] as unknown as Parameters<typeof originalOpen>);
  };

  const patchedSetRequestHeader = function (
    this: XMLHttpRequest & { __ddIntakeUrl?: string },
    name: string,
    value: string,
  ): void {
    try {
      const url = this.__ddIntakeUrl;
      if (
        url &&
        DD_INTAKE_HOST_RE.test(url) &&
        AI_CORRELATION_HEADERS.includes(String(name).toLowerCase())
      ) {
        return; // drop the correlation header on Datadog intake XHR
      }
    } catch {
      /* never let the guard break telemetry */
    }
    return originalSetRequestHeader.call(this, name, value);
  };

  (patchedOpen as { __ddIntakeGuard?: boolean }).__ddIntakeGuard = true;
  proto.open = patchedOpen as typeof proto.open;
  proto.setRequestHeader = patchedSetRequestHeader as typeof proto.setRequestHeader;
};

// NO-INFRA fix for the micro-frontend App Insights CORS problem (see
// buildDatadogProxy for the mechanism). Installs guards on BOTH `window.fetch`
// and `XMLHttpRequest` BEFORE any App Insights instance patches them (initDatadog
// runs at import time in main.tsx, ahead of every AI init). App Insights wraps
// our guards, so ours run innermost — just before the real network call — and
// for Datadog intake URLs delete the correlation headers AI injected, so the
// intake request stays CORS-"simple" and never triggers the preflight the intake
// rejects. Non-intake requests are untouched, so App Insights ↔ backend
// correlation still works. Wrapped in try/catch so a bug can never break intake.
const installDatadogIntakeHeaderGuard = (): void => {
  installFetchIntakeGuard();
  installXhrIntakeGuard();
};

export const initDatadog = (): void => {
  if (initialized) return;
  if (typeof window === 'undefined') return;

  const env = import.meta.env;

  const applicationId = env.VITE_DD_RUM_APPLICATION_ID as string | undefined;
  const clientToken = env.VITE_DD_CLIENT_TOKEN as string | undefined;
  const ddEnv = (env.VITE_DD_ENV as string) || (env.VITE_APP_ENVIRONMENT as string) || 'dev';

  // RUM runs on the deployed dev, uat, and prd environments. dev is included
  // so SRE can smoke-test RUM wiring end-to-end on a real deployment, but the
  // local `vite dev` server is always excluded (import.meta.env.DEV is only
  // true there, never for `vite build --mode <env>`) so hot-reloads and
  // throwaway local sessions never ingest. Non-allowlisted envs (test/sbx/…)
  // and vitest runs stay off too. This gate is the single source of truth:
  // even if a (public-by-design) application id + client token are present,
  // RUM stays off unless the build's env tag is allowlisted AND it's not the
  // local dev server.
  const RUM_ENABLED_ENVS = ['dev', 'uat', 'prd'];
  const isLocalDevServer = env.DEV === true;
  if (isLocalDevServer || !RUM_ENABLED_ENVS.includes(ddEnv)) {
    return;
  }

  if (!applicationId || !clientToken) {
    // We've already returned for the local `vite dev` server above, so the
    // only remaining "no creds" cases are vitest runs (MODE === 'test') and
    // deployed builds. Stay quiet for the former; warn loudly for the latter
    // so an accidentally-emptied .env.{dev,uat,prd} RUM app id / client token
    // doesn't go unnoticed in dashboards. (All three envs share one committed,
    // public-by-design RUM application; there is no per-env variable group for
    // the app id / token to check.)
    const isTestMode = (env.MODE as string) === 'test';
    if (!isTestMode) {
      console.warn(
        '[datadog] RUM/Logs NOT initialized: VITE_DD_RUM_APPLICATION_ID and/or ' +
          'VITE_DD_CLIENT_TOKEN are empty for env "' +
          ddEnv +
          '". They should be committed in the matching .env file.',
        { env: ddEnv, hasApplicationId: Boolean(applicationId), hasClientToken: Boolean(clientToken) },
      );
    }
    return;
  }

  const service = (env.VITE_DD_SERVICE as string) || 'b2o-container-app';
  const version = (env.VITE_APP_VERSION as string) || '0.0.0';
  const site = (env.VITE_DD_SITE as string) || DEFAULT_DD_SITE;

  // Optional first-party intake proxy — see buildDatadogProxy() for the full
  // rationale (micro-frontend App Insights CORS). undefined when unset (default).
  const proxy = buildDatadogProxy(env.VITE_DD_PROXY as string | undefined);

  // VITE_DD_TRACKING_CONSENT must be exactly 'granted' or 'not-granted'.
  // Anything else (including the empty default) collapses to 'granted' to
  // preserve the SDK's pre-existing behaviour. When set to 'not-granted'
  // the SDK records nothing until datadogRum.setTrackingConsent('granted')
  // is called from the app's consent flow.
  const trackingConsent: 'granted' | 'not-granted' =
    (env.VITE_DD_TRACKING_CONSENT as string) === 'not-granted' ? 'not-granted' : 'granted';

  // URLs for which RUM should attach distributed-tracing headers so traces
  // link to backend APM. Comma-separated list of http(s) origins/prefixes.
  // Defaults to the API base URLs already used by the app.
  const explicitTracing = filterTracingUrls(
    parseList(env.VITE_DD_ALLOWED_TRACING_URLS as string | undefined),
  );
  const fallbackTracing = [
    env.VITE_FOUNDATION_API_BASE_URL,
    env.VITE_EXPERIENCE_API_BASE_URL,
    env.VITE_LANGUAGE_API_URL,
  ].filter(Boolean) as string[];

  const allowedTracingUrls = (explicitTracing.length ? explicitTracing : fallbackTracing).map(
    (url) => ({
      match: url,
      propagatorTypes: ['datadog', 'tracecontext'] as Array<'datadog' | 'tracecontext'>,
    }),
  );

  // Sample rates are percentages (0-100); clamp so a fat-fingered env value
  // like '150' or '-10' can't push the SDK into an undefined state.
  const PERCENT_BOUNDS = { min: 0, max: 100 };
  const sessionSampleRate = parseNumber(
    env.VITE_DD_SESSION_SAMPLE_RATE as string,
    100,
    PERCENT_BOUNDS,
  );
  const sessionReplaySampleRate = parseNumber(
    env.VITE_DD_SESSION_REPLAY_SAMPLE_RATE as string,
    ddEnv === 'prd' ? 10 : 0,
    PERCENT_BOUNDS,
  );
  const traceSampleRate = parseNumber(
    env.VITE_DD_TRACE_SAMPLE_RATE as string,
    100,
    PERCENT_BOUNDS,
  );

  const rumConfig: RumInitConfiguration = {
    applicationId,
    clientToken,
    site,
    service,
    env: ddEnv,
    version,
    sessionSampleRate,
    sessionReplaySampleRate,
    traceSampleRate,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    // The co-resident App Insights / Piano SDKs POST telemetry continuously, and
    // the RUM SDK otherwise counts each of those as "page activity" — which keeps
    // a view perpetually busy and inflates loading_time / time-to-settle. Their
    // resource events are discarded in beforeSend (see
    // THIRD_PARTY_TELEMETRY_URL_RE); this stops them skewing view timings too.
    excludedActivityUrls: [THIRD_PARTY_TELEMETRY_URL_RE],
    // SPA: we drive named views explicitly via startView() in AppRoutes
    // (see App.tsx). With trackViewsManually=false the SDK would also
    // auto-create a view on every History push, which then gets immediately
    // replaced by our manual startView() — that produces a flood of ~1ms
    // auto-views in dashboards and inflates total-view counts ~2×.
    // The first manual view fires from AppRoutes' useEffect on mount, so
    // there is no real "early-load" gap to cover for.
    trackViewsManually: true,
    // Session Replay privacy: 'mask' (full DOM masking) is the safe default
    // for a corporate B2B app where modal/table/header content can include
    // employee data, customer names, site identifiers, etc. 'mask-user-input'
    // would only mask form fields and would record the rest of the DOM raw.
    // If a future page genuinely needs to show readable replay (e.g. a
    // public marketing landing), unmask just that subtree with the
    // data-dd-privacy="allow" attribute rather than weakening the default.
    defaultPrivacyLevel: 'mask',
    // Tracking consent: default to 'granted' to preserve the SDK's
    // pre-existing behaviour, but expose it as VITE_DD_TRACKING_CONSENT so
    // legal/privacy can flip deployed builds to 'not-granted' (which
    // halts RUM ingestion until the app calls
    // datadogRum.setTrackingConsent('granted')) without a code change.
    trackingConsent,
    allowedTracingUrls,
    // Same-origin intake proxy (opt-in). Omitted entirely when unset so the SDK
    // uses its default direct-to-Datadog transport.
    ...(proxy ? { proxy } : {}),
    // Central scrubber for everything the SDK captures natively — see redactRumEvent.
    beforeSend: redactRumEvent,
  };

  // DO NOT add 'error' here — forwardErrorsToLogs:true below already covers
  // console.error and uncaught exceptions/rejections. Adding it would
  // double-ingest every error. 'log'/'debug' are also intentionally excluded
  // (chatty @sdx/* modules would burn ingestion quota).
  const consoleLevels: Array<'info' | 'warn'> =
    ddEnv === 'prd' ? ['warn'] : ['warn', 'info'];

  const logsConfig: LogsInitConfiguration = {
    clientToken,
    site,
    service,
    env: ddEnv,
    version,
    forwardErrorsToLogs: true,
    forwardConsoleLogs: consoleLevels,
    sessionSampleRate: 100,
    // Route Logs intake through the same first-party proxy as RUM (opt-in).
    ...(proxy ? { proxy } : {}),
    // Safety net for forwardErrorsToLogs:true — see redactLogsEvent.
    beforeSend: redactLogsEvent,
  };

  try {
    // Install the fetch guard BEFORE the SDKs init so it sits innermost in the
    // fetch chain (App Insights instances that load later wrap around it). This
    // strips App-Insights correlation headers off Datadog intake POSTs so they
    // don't get preflighted+rejected — the fix that needs no infra change. The
    // optional VITE_DD_PROXY (same-origin intake) is a complementary hardening.
    installDatadogIntakeHeaderGuard();
    datadogRum.init(rumConfig);
    datadogLogs.init(logsConfig);
    initialized = true;
    if (ddEnv !== 'prd') {
      console.info('[datadog] RUM + Logs initialized', { service, env: ddEnv, version, site });
    }
  } catch (err) {
    console.error('[datadog] initialization failed', err);
  }
};

export const setDatadogUser = (user: DatadogUser): void => {
  if (!initialized) return;
  datadogRum.setUser(user);
  datadogLogs.setUser(user);
};

export const clearDatadogUser = (): void => {
  if (!initialized) return;
  datadogRum.clearUser();
  datadogLogs.clearUser();
};

export const addDatadogAction = (
  name: string,
  context?: Record<string, unknown>,
): void => {
  if (!initialized) return;
  datadogRum.addAction(name, context);
};

export const addDatadogError = (error: unknown, context?: Record<string, unknown>): void => {
  if (!initialized) return;
  datadogRum.addError(error, context);
};

export interface NormalizedTelemetryError {
  /** Lean Error safe to hand to addDatadogError/RUM (name + message only). */
  error: Error;
  /** Non-PII context fields to merge into the addDatadogError context. */
  context: { errorName: string; errorCode?: string };
}

/**
 * Normalizes an arbitrary (often MSAL / network) error into a lean Error that
 * is safe to forward to a third-party sink. Raw MSAL errors (e.g.
 * BrowserAuthError) can carry account identifiers / a typed login hint (the
 * email the user entered) in their message or enumerable fields, and Datadog
 * serializes error context onto every RUM/Log event — the same third-party PII
 * risk already fixed on setDatadogUser.
 *
 * We keep only name + errorCode + a message. Pass `genericMessage` for flows
 * where the message itself may embed user input (login) so the original text
 * (and its stack, which echoes the message) never leaves the browser. A fresh
 * Error is constructed so the original error's stack and extra own-properties
 * are dropped.
 */
export const normalizeErrorForTelemetry = (
  raw: unknown,
  options?: { genericMessage?: string },
): NormalizedTelemetryError => {
  const source = (raw ?? {}) as Record<string, unknown>;
  const name = typeof source.name === 'string' && source.name ? source.name : 'Error';
  const errorCode =
    typeof source.errorCode === 'string'
      ? source.errorCode
      : typeof source.code === 'string'
        ? source.code
        : undefined;
  const message =
    options?.genericMessage ??
    (typeof source.message === 'string' ? source.message : String(raw));

  const error = new Error(message);
  error.name = name;

  return {
    error,
    context: errorCode ? { errorName: name, errorCode } : { errorName: name },
  };
};

/**
 * Forwards a structured log entry to Datadog Logs. Used by useLogger to
 * dual-write App Insights traces into Datadog.
 */
export const addDatadogLog = (
  level: DatadogLogLevel,
  message: string,
  context?: Record<string, unknown>,
): void => {
  if (!initialized) return;
  // datadogLogs.logger has methods debug/info/warn/error matching DatadogLogLevel.
  datadogLogs.logger[level](message, context);
};

/**
 * Sets a global RUM context property that will be attached to every event
 * (views, actions, errors, resources). Use for siteId, language, etc.
 * Also mirrored onto the Logs context so logs can be filtered consistently.
 */
export const setDatadogGlobalContextProperty = (
  key: string,
  value: unknown,
): void => {
  if (!initialized) return;
  datadogRum.setGlobalContextProperty(key, value);
  datadogLogs.setGlobalContextProperty(key, value);
};

export const removeDatadogGlobalContextProperty = (key: string): void => {
  if (!initialized) return;
  datadogRum.removeGlobalContextProperty(key);
  datadogLogs.removeGlobalContextProperty(key);
};

/**
 * Clears the ENTIRE RUM + Logs global context (all `app.id` / `site.*` /
 * `user.*` keys that UserContext mirrors in). Call this on every logout /
 * auth-clear path alongside clearDatadogUser(): the SPA stays mounted after MSAL
 * logout (`onRedirectNavigate: () => false`), so React state — and therefore the
 * global-context keys — would otherwise persist and leak the previous session's
 * site/preference attributes onto the next anonymous or different-user events.
 * Clearing the whole context (rather than enumerating known keys) is robust to
 * new keys being added later. Safe no-op when DD isn't initialized.
 */
export const clearDatadogGlobalContext = (): void => {
  if (!initialized) return;
  datadogRum.clearGlobalContext();
  datadogLogs.clearGlobalContext();
};

/**
 * Starts a named RUM view. Called from AppRoutes on every route change to
 * group SPA navigations cleanly in dashboards (e.g. "/ges/about-management"
 * instead of an auto-detected URL). Per-view `service` / `version`
 * overrides exist on the underlying SDK but no caller in this codebase
 * needs them; if that changes, extend the signature deliberately rather
 * than re-exposing the full SDK shape.
 */
export const startDatadogView = (
  nameOrOptions: string | { name: string; context?: Record<string, unknown> },
): void => {
  if (!initialized) return;
  if (typeof nameOrOptions === 'string') {
    datadogRum.startView({ name: nameOrOptions });
  } else {
    datadogRum.startView(nameOrOptions as Parameters<typeof datadogRum.startView>[0]);
  }
};

export const isDatadogInitialized = (): boolean => initialized;

/**
 * Test-only helper that resets the module-level state. Not exported via
 * index.ts on purpose. Used by datadog.test.ts to verify idempotency and
 * the no-op-without-creds path independently across test cases.
 */
export const __resetDatadogForTests = (): void => {
  initialized = false;
};
